import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useStats } from "@/hooks/useStats";
import { TESTIDS } from "@/constants/testIds";
import { RULESETS } from "@/game/rulesets";
import { Trash2 } from "lucide-react";

function Stat({ label, value, mono }) {
  return (
    <Card className="p-4">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={mono ? "font-mono font-semibold text-2xl mt-1" : "font-medium text-2xl mt-1"}>{value}</p>
    </Card>
  );
}

function Bucket({ label, b }) {
  if (!b) b = { played: 0, wins: 0, losses: 0, draws: 0 };
  const wp = b.played ? Math.round((b.wins / b.played) * 100) : 0;
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-foreground/80">{label}</span>
      <span className="font-mono text-muted-foreground">
        {b.wins}-{b.losses}-{b.draws} ({wp}%)
      </span>
    </div>
  );
}

export function Statistics() {
  const { stats, reset } = useStats();
  const totals = stats.totals;
  const wp = totals.played ? Math.round((totals.wins / totals.played) * 100) : 0;

  return (
    <Layout>
      <section
        data-testid={TESTIDS.statsPage}
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
      >
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Local statistics</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-1">Your record</h1>
          </div>
          <Button
            data-testid={TESTIDS.statsReset}
            variant="outline"
            onClick={() => {
              if (confirm("Reset all local statistics? This cannot be undone.")) reset();
            }}
            className="rounded-full"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Reset stats
          </Button>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Games played" value={totals.played} mono />
          <Stat label="Wins" value={totals.wins} mono />
          <Stat label="Losses" value={totals.losses} mono />
          <Stat label="Draws" value={totals.draws} mono />
          <Stat label="Win %" value={`${wp}%`} mono />
          <Stat label="Current streak" value={stats.currentStreak || 0} mono />
          <Stat label="Longest streak" value={stats.longestStreak || 0} mono />
          <Stat label="Largest capture" value={stats.largestCapture || 0} mono />
        </div>

        <div className="mt-10 grid sm:grid-cols-2 gap-6">
          <Card className="p-5">
            <p className="font-display text-xl mb-3">By difficulty</p>
            <div className="space-y-2">
              <Bucket label="Beginner" b={stats.byDifficulty.beginner} />
              <Bucket label="Intermediate" b={stats.byDifficulty.intermediate} />
              <Bucket label="Advanced" b={stats.byDifficulty.advanced} />
            </div>
          </Card>
          <Card className="p-5">
            <p className="font-display text-xl mb-3">By ruleset</p>
            <div className="space-y-2">
              {RULESETS.map((r) => (
                <Bucket key={r.id} label={r.name} b={stats.byRuleset[r.id]} />
              ))}
            </div>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
